import { ArrowRight, CheckCircle2, Droplet, Warehouse, Wrench } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/Button";
import {
  MAQUINARIO_STATUS_LABELS,
  type ContratoDto,
  type LocacaoDto,
  type ManutencaoDto,
  type MaquinarioDto,
} from "@/services/locacoes";
import { MAQUINARIO_STATUS_THEME } from "./statusTheme";
import {
  filterMaquinasNaGaragem,
  getUltimaLocacaoEncerrada,
  hasManutencaoAbertaMaquina,
  locacoesSectionTitleClass,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
} from "./locacoesUtils";
import { formatDate, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import { LocacoesStatCard } from "./LocacoesStatCard";

interface Props {
  maquinarios: MaquinarioDto[];
  contratos: ContratoDto[];
  locacoes: LocacaoDto[];
  manutencoes: ManutencaoDto[];
  onVerMaquina: (m: MaquinarioDto) => void;
  onNovoAbastecimento: (m: MaquinarioDto) => void;
  onRegistrarManutencao: (m: MaquinarioDto) => void;
}

export function TabGaragem({
  maquinarios,
  contratos,
  locacoes,
  manutencoes,
  onVerMaquina,
  onNovoAbastecimento,
  onRegistrarManutencao,
}: Props) {
  const naGaragem = filterMaquinasNaGaragem(maquinarios, locacoes, contratos);

  const kpi = useMemo(() => {
    const comManutencao = naGaragem.filter((m) => hasManutencaoAbertaMaquina(m.id, manutencoes)).length;
    const disponiveis = naGaragem.filter((m) => m.status === "disponivel").length;
    const emManutencao = naGaragem.filter((m) => m.status === "manutencao").length;
    return { total: naGaragem.length, comManutencao, disponiveis, emManutencao };
  }, [naGaragem, manutencoes]);

  return (
    <div className={locacoesTabContentClass}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LocacoesStatCard
          title="Em pátio"
          value={kpi.total}
          icon={<Warehouse className="h-5 w-5" />}
          accentClass="border-l-4 border-l-sky-400"
        />
        <LocacoesStatCard
          title="Disponíveis"
          value={kpi.disponiveis}
          icon={<CheckCircle2 className="h-5 w-5" />}
          accentClass="border-l-4 border-l-emerald-400"
        />
        <LocacoesStatCard
          title="Em manutenção"
          value={kpi.emManutencao}
          icon={<Wrench className="h-5 w-5" />}
          accentClass="border-l-4 border-l-amber-400"
        />
        <LocacoesStatCard
          title="OS em aberto"
          value={kpi.comManutencao}
          icon={<Wrench className="h-5 w-5" />}
          accentClass="border-l-4 border-l-rose-400"
        />
      </div>

      <section className="flex flex-col gap-4">
        <div className={locacoesSectionTitleClass}>
          <Warehouse className="h-4 w-4 text-brand-600" />
          <h3>Ativos em pátio ({naGaragem.length})</h3>
        </div>
        <p className="-mt-2 text-sm text-slate-500">
          Sem vínculo com contrato ativo. Registre abastecimentos e manutenções pela coluna Ações ou abra Detalhes para o histórico
          completo.
        </p>

        <div className={locacoesTableWrapClass}>
          <table className="min-w-full text-sm">
            <thead className={locacoesTableHeadClass}>
              <tr>
                <th className="px-4 py-3">Ativo</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3">Horímetro</th>
                <th className="px-4 py-3">Última locação</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Manutenção</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {naGaragem.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    Nenhum ativo em pátio no momento.
                  </td>
                </tr>
              ) : (
                naGaragem.map((m) => {
                  const theme = MAQUINARIO_STATUS_THEME[m.status];
                  const ultimaLoc = getUltimaLocacaoEncerrada(m.id, locacoes);
                  const manutAberta = hasManutencaoAbertaMaquina(m.id, manutencoes);
                  return (
                    <tr key={m.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{m.nome}</div>
                        {m.codigoInterno && <div className="text-xs text-slate-500">{m.codigoInterno}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{m.tipo || "—"}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-700">{formatNumber(m.horimetroAtual, 1)} h</td>
                      <td className="px-4 py-3 text-slate-600">
                        {ultimaLoc ? formatDate(ultimaLoc.dataFimReal ?? ultimaLoc.dataInicio) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                            theme.badge,
                          )}
                        >
                          <span className={cn("h-1.5 w-1.5 rounded-full", theme.dot)} />
                          {MAQUINARIO_STATUS_LABELS[m.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {manutAberta ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900">
                            <Wrench className="h-3 w-3" />
                            Em aberto
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onNovoAbastecimento(m)}
                            leftIcon={<Droplet className="h-3.5 w-3.5" />}
                          >
                            Abastecer
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onRegistrarManutencao(m)}
                            leftIcon={<Wrench className="h-3.5 w-3.5" />}
                          >
                            Manutenção
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onVerMaquina(m)}
                            rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
                          >
                            Detalhes
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
